import { ICommand } from '../../../core/commands/ICommand';
import { CreateShapeCommand } from '../../../core/commands/CreateShapeCommand';
import type { ProcessActivityType } from '../shapes/flowchart/flowchartShapes.manifest';
import { createNewRecordBinding } from '../../data-binding/utils/createNewRecordBinding';
import { createNodeId, type NodeAddition } from '../../../core/data-profiles/scenarios/ScenarioDelta';
import type { CommandDeltaContribution, IScenarioRecordable } from '../../../core/data-profiles/scenarios/ScenarioRecordable';
import { getTranslatedFlowchartShapeLabel } from '../shapes/flowchart/flowchartShapeI18n';
import { snapPointToGrid } from '../../../lib/diagram/gridSnap';

export interface CreateProcessStepParams {
  initialStyle?: Readonly<Record<string, unknown>>;
  x: number;
  y: number;
  width?: number;
  height?: number;
  label?: string;
  description?: string;
  activityType?: ProcessActivityType;
  /** Optional: data source ID for auto-creating repository binding */
  dataSourceId?: string;
  /** Optional: user ID for tracking who created the record */
  userId?: string;
  /** Optional: parent record ID for creating child records in repository tree */
  parentRecordId?: string;
}

export class CreateProcessStepCommand implements ICommand, IScenarioRecordable {
  id: string;
  name = 'Create Process Step';
  description?: string;
  timestamp: number;
  readonly isScenarioRecordable = true as const;

  private params: CreateProcessStepParams;
  private inner?: CreateShapeCommand;
  private createdBinding?: ReturnType<typeof createNewRecordBinding>;
  private scenarioNodeId: ReturnType<typeof createNodeId>;

  constructor(params: CreateProcessStepParams) {
    this.id = `create-process-step-${Date.now()}`;
    this.timestamp = Date.now();
    this.params = params;
    this.description = 'Create a process step';
    this.scenarioNodeId = createNodeId(`scenario-${this.id}`);
  }

  execute(): void {
    const defaultProcessLabel = getTranslatedFlowchartShapeLabel('process');
    const snappedPosition = snapPointToGrid({
      x: this.params.x,
      y: this.params.y,
    });

    const attrs: Record<string, unknown> = {
      label: this.params.label ?? defaultProcessLabel,
      description: this.params.description ?? '',
      activityType: this.params.activityType ?? 'task',
    };

    // If dataSourceId is provided, create auto-binding for new repository record
    if (this.params.dataSourceId) {
      // Generate a temporary shape ID for the binding
      const tempShapeId = `shape-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const initialData: Record<string, unknown> = {
        name: this.params.label ?? defaultProcessLabel,
        description: this.params.description ?? '',
      };
      if (this.params.parentRecordId) {
        initialData.parentId = this.params.parentRecordId;
      }

      this.createdBinding = createNewRecordBinding({
        shapeId: tempShapeId,
        sourceId: this.params.dataSourceId,
        userId: this.params.userId ?? 'unknown',
        initialData,
      });

      // Add the binding to attrs so it's stored with the shape
      attrs.dataBinding = this.createdBinding;
    }

    this.inner = new CreateShapeCommand({
      initialStyle: this.params.initialStyle,
      type: 'process',
      category: 'flowchart',
      x: snappedPosition.x,
      y: snappedPosition.y,
      width: this.params.width,
      height: this.params.height,
      attrs,
    } as any);

    this.inner.execute();
  }

  undo(): void {
    this.inner?.undo();
    this.createdBinding = undefined;
  }

  redo(): void {
    this.inner?.redo();
  }

  getCreatedId(): string | undefined {
    return this.inner?.getCreatedId?.();
  }

  getCreatedBinding(): ReturnType<typeof createNewRecordBinding> | undefined {
    return this.createdBinding;
  }

  toScenarioDelta(): CommandDeltaContribution {
    const label = this.params.label ?? getTranslatedFlowchartShapeLabel('process');
    const snappedPosition = snapPointToGrid({
      x: this.params.x,
      y: this.params.y,
    });
    const props: Record<string, unknown> = {
      description: this.params.description ?? '',
      activityType: this.params.activityType ?? 'task',
    };

    const addition: NodeAddition = {
      id: this.scenarioNodeId,
      type: 'process',
      name: label,
      position: snappedPosition,
      props,
    };

    return { additions: [addition] };
  }

  getUndoDelta(): CommandDeltaContribution {
    return {
      removals: [
        {
          nodeId: this.scenarioNodeId,
          originalData: {
            name: this.params.label ?? getTranslatedFlowchartShapeLabel('process'),
            type: 'process',
          },
        },
      ],
    };
  }
}
